import { Button } from "@msuite/ui/button";
import { useFileUpload } from "@msuite/ui/file-upload";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type WorkspaceSchema, workspaceSchema } from "@msuite/utils/schemas";
import type { Tables } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { WorkspaceForm } from "@/forms/workspace-form";
import { client } from "@/hono";

/** Props Interface */
interface AddWorkspaceModalProps extends ModalProps {
	onComplete: (workspace: Tables<"workspaces">) => Promise<void> | void;
}

export function AddWorkspaceModal({ isOpen, onClose, onComplete }: AddWorkspaceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		defaultValues: {
			color: "accent",
			region: null,
			businessHours: null,
		},
		resolver: resolver(workspaceSchema),
	});

	/** Uploaders */
	const { uploadFn } = useUpload();
	const workspaceLogoFileController = useFileUpload({
		uploadFn,
	});
	const workspaceLetterpaperFileController = useFileUpload({
		uploadFn,
	});

	/** Functions */
	async function handleOnSubmit(json: WorkspaceSchema) {
		try {
			const workspaceLetterpaperUploadResults =
				await workspaceLetterpaperFileController.handleOnFileUpload();
			const workspaceLogoUploadResults = await workspaceLogoFileController.handleOnFileUpload();
			const response = await client.organizations.workspaces.$post({
				json: {
					...json,
					workspaceLetterpaper: {
						uploadResults: workspaceLetterpaperUploadResults,
					},
					workspaceLogo: {
						uploadResults: workspaceLogoUploadResults,
					},
				},
			});
			const data = await responseHandler(response);
			if (!data.workspace) throw new Error("Workspace not found");
			await onComplete(data.workspace);
			handleOnClose();
		} catch (error) {
			toast.error(t("general.something_went_wrong"));
			console.error(error);
		}
	}

	function handleOnClose() {
		methods.reset();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={handleOnClose}
				title={t("organization.add_workspace")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				data-cy="add-workspace-modal"
			>
				<Modal.Body>
					<WorkspaceForm
						workspaceLogoFileController={workspaceLogoFileController}
						workspaceLetterpaperFileController={workspaceLetterpaperFileController}
						workspaceId={null}
					/>
				</Modal.Body>
				<Modal.Footer>
					<Button
						onClick={handleOnClose}
						variant="ghost"
					>
						{t("general.cancel")}
					</Button>
					<Button
						type="submit"
						variant="primary"
						isLoading={methods.formState.isSubmitting}
					>
						{t("general.submit")}
					</Button>
				</Modal.Footer>
			</Modal>
		</FormProvider>
	);
}
